import {useRef} from 'react'
import {TableIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Link} from '@primer/react'
import {SkeletonText, Stack} from '@primer/react/experimental'

import type {
  PullRequestProject,
  PullRequestProjectStatus,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/projects'
import {useUpdateProjectStatus} from '../../mutations/use-update-project-status'
import {useProjectsQuery} from './data/route-queries'
import {MutationFailureBanner} from '../FailureBanner'
import sidebarListStyles from './SidebarActionList.module.css'
import styles from './Projects.module.css'

export function Projects() {
  const {data, isPending, isError} = useProjectsQuery()
  if (isPending) return <SkeletonProjects />
  if (isError) return <span className={sidebarListStyles.emptyState}>Unable to load projects</span>

  const projects = data?.projects
  if (!projects || projects.length === 0) {
    return <span className={sidebarListStyles.emptyState}>None yet</span>
  }

  return (
    <ul className={styles.list}>
      {[...projects].reverse().map(project => (
        <ProjectRow key={project.id} project={project} />
      ))}
    </ul>
  )
}

// Renders each project as a bordered card -- a title row (link + closed badge)
// and, below it, the project's "Status" field -- mirroring the Rails sidebar's
// `issues/sidebar/_memex_project_item` partial + `Sidebar::MemexStatusComponent`.
function ProjectRow({project}: {project: PullRequestProject}) {
  return (
    <li className={styles.card}>
      <div className={styles.header}>
        <Link href={project.url} className={styles.titleLink} muted>
          <TableIcon className={styles.titleIcon} />
          {project.title}
        </Link>
        {project.closed && <span className={styles.closedBadge}>Closed</span>}
      </div>
      {project.status && <ProjectStatusField project={project} status={project.status} />}
    </li>
  )
}

function ProjectStatusField({project, status}: {project: PullRequestProject; status: PullRequestProjectStatus}) {
  const updateStatusMutation = useUpdateProjectStatus()
  const {mutate, isPending} = updateStatusMutation
  const statusButtonRef = useRef<HTMLButtonElement>(null)
  const currentOption = status.options.find(option => option.id === status.optionId)
  const label = currentOption?.name ?? 'No status'

  if (!project.viewerCanUpdate) {
    return (
      <div className={styles.statusRow}>
        <span className={styles.statusText}>Status: {label}</span>
      </div>
    )
  }

  return (
    <div className={styles.statusRow}>
      <ActionMenu>
        <ActionMenu.Button
          ref={statusButtonRef}
          variant="invisible"
          size="small"
          trailingAction={TriangleDownIcon}
          className={styles.statusButton}
          aria-label={`Status: ${label} for ${project.title}`}
          inactive={isPending || undefined}
        >
          Status: {label}
        </ActionMenu.Button>
        <ActionMenu.Overlay align="start">
          <ActionList selectionVariant="single">
            {status.options.map(option => (
              <ActionList.Item
                key={option.id}
                selected={option.id === status.optionId}
                onSelect={() => {
                  if (isPending || option.id === status.optionId) return
                  mutate({projectId: project.id, itemId: project.itemId, fieldId: status.fieldId, optionId: option.id})
                }}
              >
                {option.name}
              </ActionList.Item>
            ))}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <MutationFailureBanner
        mutation={updateStatusMutation}
        title="Failed to update project status"
        returnFocusRef={statusButtonRef}
        className={styles.mutationError}
      />
    </div>
  )
}

function SkeletonProjects() {
  return (
    <Stack direction="vertical" gap="condensed">
      <span className="sr-only">Loading...</span>
      <SkeletonText width="10ch" />
      <SkeletonText width="7ch" />
    </Stack>
  )
}
